import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';
import metricsAPI from '@/api/metrics';
import { normalizeData, normalizeIncluded } from '@/utils/jsonapi';
import { getApiErrorMessage } from '@/utils/apiErrors'
import { TENANT_SWITCH_FULFILLED } from '@features/auth/tenantSwitchActionType';
import { accessAudiencePayload } from '@/utils/accessAudience';
import { METRIC_UNITS } from './metricUnits';

const UNIT_TO_METRIC_TYPE = Object.fromEntries(METRIC_UNITS.map(({ value, metricType }) => [value, metricType]));

const METRIC_TYPE_TO_UNIT = Object.fromEntries(METRIC_UNITS.map(({ value, metricType }) => [metricType, value]));

const mapUnitToMetricType = (unit) => UNIT_TO_METRIC_TYPE[unit] || 'COUNT';

const mapMetricTypeToUnit = (metricType) => METRIC_TYPE_TO_UNIT[metricType] || 'Count';

/**
 * Normalize a metric object from the API, ensuring consistent field formats.
 * Uppercases metric_type so all components can compare against uppercase enums
 * regardless of what casing the backend sends.
 */
const normalizeMetric = (metric) => {
  if (!metric) return metric;
  return {
    ...metric,
    metric_type: metric.metric_type?.toUpperCase() ?? metric.metric_type,
  };
};

/**
 * Entity adapter for metrics
 */
const metricsAdapter = createEntityAdapter({
  selectId: (metric) => metric.id,
  sortComparer: (a, b) => (a.name || '').localeCompare(b.name || ''),
});

/**
 * Async thunk to fetch all metrics
 * Normalizes JSON:API response before storing in Redux
 *
 * @param {Object} [queryOptions] - JSON:API query options
 * @param {Object} [queryOptions.filters] - Filter criteria (e.g., { category: 'uuid', is_active: true })
 * @param {Array<string>|string} [queryOptions.sort] - Sort fields (e.g., ['name', '-created_at'])
 * @param {Array<string>|string} [queryOptions.include] - Relations to include (e.g., ['category', 'created_by'])
 * @param {number} [queryOptions.page] - Explicit single page; omit to load the complete catalog
 * @param {number} [queryOptions.pageSize] - Items per page
 */
export const fetchMetricsThunk = createAsyncThunk(
  'metrics/fetchAll',
  async (queryOptions = {}, { rejectWithValue, getState, signal }) => {
    try {
      // Every page belongs to the organization that started the request,
      // even if the user switches organizations while it is loading.
      const tenantId = getState().auth?.currentTenantId;
      const requestOptions = { signal, ...(tenantId ? { headers: { 'X-Tenant-ID': tenantId } } : {}) };
      const response = queryOptions.page != null
        ? await metricsAPI.getAll(queryOptions, requestOptions)
        : await metricsAPI.getAllPages(queryOptions, requestOptions);

      // Handle both DRF pagination and JSON:API formats
      // Backend returns: { count, next, previous, results: [JSON:API objects with 'data' key] }
      // OR: { data: [JSON:API objects] }
      const results = response.results || response.data || [];

      // Normalize each JSON:API resource in the results array
      const normalized = (normalizeData(results) || []).map(normalizeMetric);

      return {
        data: normalized,
        count: response.meta?.pagination?.count ?? response.count,
        next: response.links?.next ?? response.next,
        previous: response.links?.prev ?? response.previous,
        included: response.included // Pass through for components that need it
      };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

/**
 * Async thunk to fetch a single metric by ID
 * Normalizes JSON:API response before storing in Redux
 */
export const fetchMetricDetailThunk = createAsyncThunk(
  'metrics/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await metricsAPI.getById(id);

      // Normalize single resource
      const normalized = normalizeMetric(normalizeData(jsonApiResponse.data));

      // Store the full included metric_partition data for use by selectors
      const included = normalizeIncluded(jsonApiResponse.included || []);
      if (included.metric_partition) {
        const metricPartitions = Object.values(included.metric_partition);

        if (metricPartitions.length > 0) {
          normalized._metricPartitions = metricPartitions;
        }
      }

      return normalized;
    } catch (error) {
      // Carries status alongside the message so the edit page can tell a 404
      // (metric genuinely doesn't exist) apart from a 500/403 (server or
      // permission failure) instead of rendering "Metric Not Found" for both.
      return rejectWithValue({
        message: getApiErrorMessage(error),
        status: error?.apiError?.status ?? null,
      });
    }
  }
);

/**
 * Async thunk to create a new metric
 * Normalizes JSON:API response before storing in Redux
 */
export const createMetricThunk = createAsyncThunk(
  'metrics/create',
  async (data, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await metricsAPI.create(data);
      return normalizeMetric(normalizeData(jsonApiResponse.data));
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

/**
 * Async thunk to update an existing metric
 * Normalizes JSON:API response before storing in Redux
 */
export const updateMetricThunk = createAsyncThunk(
  'metrics/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await metricsAPI.update(id, data);

      // Process included resources (partition_configurations, partition_types, etc.)
      const included = normalizeIncluded(jsonApiResponse.included || []);

      // Normalize single resource
      const normalized = normalizeMetric(normalizeData(jsonApiResponse.data));

      // Store the full included metric_partition data for use by selectors
      // Use normalizeIncluded utility instead of manual extraction
      if (included.metric_partition) {
        const metricPartitions = Object.values(included.metric_partition);

        if (metricPartitions.length > 0) {
          normalized._metricPartitions = metricPartitions;
        }
      }

      return normalized;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

/**
 * Async thunk to delete a metric
 */
export const deleteMetricThunk = createAsyncThunk(
  'metrics/delete',
  async (id, { rejectWithValue }) => {
    try {
      await metricsAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

/**
 * Code the API returns when a metric cannot be deactivated because something
 * still refers to it. Exported so the UI can tell that case apart from an
 * ordinary failure and show what is in the way.
 */
export const METRIC_IN_USE = 'metric_in_use';

/**
 * Async thunk to set a metric's activation state.
 *
 * Activation has its own endpoints and its own role (Administrator); the API
 * refuses a PATCH that names is_active, so this is the only route. Takes the
 * desired state rather than toggling, so a stale list cannot flip the wrong way.
 */
export const setMetricActiveThunk = createAsyncThunk(
  'metrics/setActive',
  async ({ id, isActive }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = isActive
        ? await metricsAPI.activate(id)
        : await metricsAPI.deactivate(id);

      return normalizeMetric(normalizeData(jsonApiResponse.data));
    } catch (error) {
      // Deactivation is refused while a dashboard, report, form or sync still
      // uses the metric, and the refusal carries the list of what. Flattening
      // that to a string would throw away the only actionable part of it
      // (graphit-backend#255), so the structured error is kept.
      const apiError = error?.apiError || error?.response?.data;
      const firstError = apiError?.errors?.[0];
      return rejectWithValue({
        code: firstError?.code || 'metric_activation_failed',
        message: getApiErrorMessage(error),
        usage: firstError?.meta?.usage || [],
      });
    }
  }
);

/**
 * Async thunk to create metric from wizard form data
 * Accepts wizard payload: { name, description, unit, is_active, category_id, partition_types: [{partition_type_id, is_required}] }
 * Formats data for API and handles wizard-specific validation
 */
export const createMetricFromWizardThunk = createAsyncThunk(
  'metrics/createFromWizard',
  async (wizardData, { rejectWithValue }) => {
    try {
      // Format wizard data for API
      // Map frontend field names to backend field names:
      // - unit -> metric_type (backend uses metric_type with uppercase enum values)
      // - partition_types -> partition_configurations (backend relationship name)
      const apiData = {
        name: wizardData.name,
        description: wizardData.description || '',
        metric_type: mapUnitToMetricType(wizardData.unit),  // Convert frontend unit to backend metric_type
        aggregation_mode: wizardData.aggregation_mode || 'sum',
        calculation_config: wizardData.calculation_config || {},
        is_active: wizardData.is_active !== false, // Default to true
        category: wizardData.category_id || null,
        partition_configurations: (wizardData.partition_types || []).map(pt => ({
          partition_type: pt.partition_type_id,
          is_required: pt.is_required || false,
        })),
      };

      const jsonApiResponse = await metricsAPI.create(apiData);
      const normalized = normalizeMetric(normalizeData(jsonApiResponse.data));

      // Process included metric_partition relationships
      const included = normalizeIncluded(jsonApiResponse.included || []);
      if (included.metric_partition) {
        const metricPartitions = Object.values(included.metric_partition);
        if (metricPartitions.length > 0) {
          normalized._metricPartitions = metricPartitions;
        }
      }

      return normalized;
    } catch (error) {
      // Enhanced error parsing for wizard validation
      let errorMessage = getApiErrorMessage(error);

      // Check for field-specific validation errors
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const nameError = errors.find(e =>
          e.source?.pointer === '/data/attributes/name' || e.detail?.includes('name')
        );
        if (nameError) {
          errorMessage = 'Metric name already exists or is invalid';
        }
      }

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Codes the API returns from `POST /metrics/from-template/` with
 * `source: 'derived'` (E1/E2E-02), as a plain `JsonResponse` rather than the
 * JSON:API `errors[]` envelope - see `createMetricFromTemplateThunk`.
 */
export const METRIC_TEMPLATE_MISSING_INPUTS = 'missing_inputs';
export const METRIC_TEMPLATE_ALREADY_EXISTS = 'already_exists';

/**
 * Async thunk to create a metric from a starter template (design doc §3;
 * E1/E2E-02 add `source: 'derived'`), with or without seeded sample data.
 * `meta.sample_entries_created` (from the response, not the normalized
 * metric) is returned alongside the metric so `CreateFromTemplateDialog`'s
 * success state can say how many weeks it seeded without a second round trip.
 *
 * @param {Object} params
 * @param {string} params.templateKey
 * @param {boolean} [params.seedSampleData=false]
 * @param {'manual'|'derived'} [params.source='manual']
 */
export const createMetricFromTemplateThunk = createAsyncThunk(
  'metrics/createFromTemplate',
  async ({ templateKey, seedSampleData = false, source = 'manual' }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await metricsAPI.createFromTemplate({
        templateKey,
        seedSampleData,
        source,
      });
      const metric = normalizeMetric(normalizeData(jsonApiResponse.data));
      return { metric, sampleEntriesCreated: jsonApiResponse.meta?.sample_entries_created ?? 0 };
    } catch (error) {
      // `source: 'derived'` answers 409 as a flat JsonResponse body
      // (core/views.py's MetricViewSet.from_template, bypassing the JSON:API
      // errors[] envelope on purpose so this exact shape survives):
      // `{error: 'missing_inputs', inputs: [refs]}` when a required input
      // metric doesn't exist yet, or `{error: 'already_exists', ...}` when a
      // concurrent request already created this metric. Flattening either to
      // a string (getApiErrorMessage's normal job) would throw away what the
      // picker needs to act on it, so both are kept structured - the same
      // pattern setMetricActiveThunk uses for METRIC_IN_USE.
      const code = error?.response?.data?.error;
      if (code === METRIC_TEMPLATE_MISSING_INPUTS) {
        return rejectWithValue({
          code,
          inputs: error.response.data.inputs || [],
          message: getApiErrorMessage(error, 'This metric needs other metrics set up first.'),
        });
      }
      if (code === METRIC_TEMPLATE_ALREADY_EXISTS) {
        // A9's confirmed shape: {error: 'already_exists', metric_id, name} -
        // `existing_metric_for_template` runs before the manual/derived
        // branch, so this can arrive from either source.
        const existingName = error.response.data.name;
        return rejectWithValue({
          code,
          metricId: error.response.data.metric_id || error.response.data.metric?.id || null,
          message: getApiErrorMessage(
            error,
            existingName ? `"${existingName}" already exists.` : 'This metric already exists.'
          ),
        });
      }
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

/**
 * Async thunk to clear a metric's `is_sample` entries (design doc §3's
 * "Clear sample data" action). The metric itself is kept; only its sample
 * entries go, so this returns the updated metric (its `has_sample_data`
 * flag flipped) rather than the deleted entry ids.
 *
 * @param {string} metricId
 */
export const clearSampleDataThunk = createAsyncThunk(
  'metrics/clearSampleData',
  async (metricId, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await metricsAPI.clearSampleData(metricId);
      return normalizeMetric(normalizeData(jsonApiResponse.data));
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

/**
 * Async thunk to update metric from edit form
 * Accepts only changed fields for optimized API payload
 * Handles metric edit form submission with partial updates
 */
export const updateMetricFromEditFormThunk = createAsyncThunk(
  'metrics/updateFromEditForm',
  async ({ id, changedFields }, { rejectWithValue }) => {
    try {
      // Only send changed fields to API
      // Map frontend field names to backend field names
      const apiData = {};

      if ('name' in changedFields) apiData.name = changedFields.name;
      if ('description' in changedFields) apiData.description = changedFields.description;
      if ('unit' in changedFields) apiData.metric_type = mapUnitToMetricType(changedFields.unit);
      if ('is_active' in changedFields) apiData.is_active = changedFields.is_active;
      if ('category' in changedFields) apiData.category = changedFields.category;
      if ('aggregation_mode' in changedFields) apiData.aggregation_mode = changedFields.aggregation_mode;
      if ('calculation_config' in changedFields) apiData.calculation_config = changedFields.calculation_config;
      if ('access_audience' in changedFields) Object.assign(apiData, accessAudiencePayload(changedFields.access_audience));

      // Handle partition_types updates -> partition_configurations
      if ('partition_types' in changedFields) {
        apiData.partition_configurations = changedFields.partition_types.map(pt => ({
          partition_type: pt.partition_type_id || pt.partition_type,
          is_required: pt.is_required || false,
        }));
      }

      const jsonApiResponse = await metricsAPI.update(id, apiData);
      const normalized = normalizeMetric(normalizeData(jsonApiResponse.data));

      // Process included metric_partition relationships
      const included = normalizeIncluded(jsonApiResponse.included || []);
      if (included.metric_partition) {
        const metricPartitions = Object.values(included.metric_partition);
        if (metricPartitions.length > 0) {
          normalized._metricPartitions = metricPartitions;
        }
      }

      return normalized;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

/**
 * Metrics slice with state management for metric CRUD operations
 */
const metricsSlice = createSlice({
  name: 'metrics',
  initialState: metricsAdapter.getInitialState({
    currentMetric: null,
    catalogRequestId: null,
    loading: false,
    error: null,
    detailError: null,
  }),
  reducers: {
    setCurrentMetric: (state, action) => {
      state.currentMetric = action.payload;
    },
    /**
     * Upsert many metrics (for entity processor)
     */
    upsertMany: (state, action) => {
      metricsAdapter.upsertMany(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Metrics
      .addCase(fetchMetricsThunk.pending, (state, action) => {
        state.catalogRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMetricsThunk.fulfilled, (state, action) => {
        if (state.catalogRequestId !== action.meta.requestId) return;
        state.catalogRequestId = null;
        state.loading = false;
        metricsAdapter.setAll(state, action.payload?.data || []);
      })
      .addCase(fetchMetricsThunk.rejected, (state, action) => {
        if (state.catalogRequestId !== action.meta.requestId) return;
        state.catalogRequestId = null;
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Detail
      .addCase(fetchMetricDetailThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.detailError = null;
      })
      .addCase(fetchMetricDetailThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentMetric = action.payload;
        metricsAdapter.upsertOne(state, action.payload);
      })
      .addCase(fetchMetricDetailThunk.rejected, (state, action) => {
        state.loading = false;
        // state.error stays a plain string for MetricDetailPage's existing
        // `${error}` interpolation; detailError carries the status so the
        // edit page can tell a 404 apart from a 500/403 (feedback M-1).
        state.error = action.payload?.message ?? null;
        state.detailError = action.payload ?? null;
      })
      // Create
      .addCase(createMetricThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMetricThunk.fulfilled, (state, action) => {
        state.loading = false;
        metricsAdapter.addOne(state, action.payload);
        state.currentMetric = action.payload; // Set as current so detail page can access it
      })
      .addCase(createMetricThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update - don't set loading to true to prevent list flash
      // The upsertOne in fulfilled will update the entity in place
      .addCase(updateMetricThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(updateMetricThunk.fulfilled, (state, action) => {
        state.loading = false;
        metricsAdapter.upsertOne(state, action.payload);
        if (state.currentMetric?.id === action.payload.id) {
          state.currentMetric = action.payload;
        }
      })
      .addCase(updateMetricThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Activation - same no-flash handling as update
      .addCase(setMetricActiveThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(setMetricActiveThunk.fulfilled, (state, action) => {
        state.loading = false;
        metricsAdapter.upsertOne(state, action.payload);
        if (state.currentMetric?.id === action.payload.id) {
          state.currentMetric = action.payload;
        }
      })
      .addCase(setMetricActiveThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete (soft delete - sets is_active=false on backend)
      // We remove from state immediately for UX, but it will reappear if filtered to show inactive
      .addCase(deleteMetricThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteMetricThunk.fulfilled, (state, action) => {
        // Get the current metric to update is_active flag
        const existingMetric = state.entities[action.payload];
        if (existingMetric) {
          // Update the metric's is_active flag instead of removing
          // This matches the backend's soft delete behavior
          metricsAdapter.updateOne(state, {
            id: action.payload,
            changes: { is_active: false }
          });
        }
        if (state.currentMetric?.id === action.payload) {
          state.currentMetric = null;
        }
      })
      .addCase(deleteMetricThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create From Wizard
      .addCase(createMetricFromWizardThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMetricFromWizardThunk.fulfilled, (state, action) => {
        state.loading = false;
        metricsAdapter.addOne(state, action.payload);
        state.currentMetric = action.payload;
      })
      .addCase(createMetricFromWizardThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create From Template (design doc §3) - the fulfilled payload wraps
      // the metric alongside `sampleEntriesCreated`, so only `.metric` goes
      // into the entity adapter.
      .addCase(createMetricFromTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMetricFromTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;
        metricsAdapter.addOne(state, action.payload.metric);
      })
      .addCase(createMetricFromTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Clear Sample Data - no loading flash, same as update/activation
      .addCase(clearSampleDataThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(clearSampleDataThunk.fulfilled, (state, action) => {
        metricsAdapter.upsertOne(state, action.payload);
        if (state.currentMetric?.id === action.payload.id) {
          state.currentMetric = action.payload;
        }
      })
      .addCase(clearSampleDataThunk.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Update From Edit Form
      .addCase(updateMetricFromEditFormThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMetricFromEditFormThunk.fulfilled, (state, action) => {
        state.loading = false;
        metricsAdapter.upsertOne(state, action.payload);
        if (state.currentMetric?.id === action.payload.id) {
          state.currentMetric = action.payload;
        }
      })
      .addCase(updateMetricFromEditFormThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Clear metrics when the tenant switches - otherwise the previous
      // tenant's entities (and any open detail/edit route's id) survive into
      // the new tenant and the first refetch 404s against data that looks
      // like it should exist (feedback M-1's tenant-switch reproduction).
      .addCase(TENANT_SWITCH_FULFILLED, (state) => {
        state.catalogRequestId = null;
        metricsAdapter.removeAll(state);
        state.currentMetric = null;
        state.error = null;
        state.detailError = null;
        state.loading = false;
      });
  },
});

export const { setCurrentMetric, upsertMany } = metricsSlice.actions;

// Entity adapter selectors with safe defaults
const getMetricsState = (state) => {
  return state.metrics || metricsAdapter.getInitialState();
};

export const {
  selectAll: selectAllMetrics,
  selectById: selectMetricById,
  selectIds: selectMetricIds,
} = metricsAdapter.getSelectors(getMetricsState);

// Backward compatibility selector
export const selectMetrics = selectAllMetrics;

// Custom selectors with safe defaults
export const selectCurrentMetric = (state) => state.metrics?.currentMetric || null;
export const selectMetricsLoading = (state) => state.metrics?.loading || false;
export const selectMetricsError = (state) => state.metrics?.error || null;
/** { message, status } from the most recent fetchMetricDetailThunk rejection, or null. */
export const selectMetricDetailError = (state) => state.metrics?.detailError || null;

/**
 * Selector to get current metric with enriched partition configurations
 * Returns metric with partition_configurations containing full data
 */
export const selectCurrentMetricWithPartitions = (state) => {
  const metric = state.metrics?.currentMetric;
  if (!metric || !metric._metricPartitions) {
    return metric;
  }

  // Enrich partition_configurations with full metric_partition data
  return {
    ...metric,
    partition_configurations: metric._metricPartitions || []
  };
};

/**
 * Selector to get metric formatted for edit form population
 * Denormalizes metric data for form defaultValues
 * Returns: { name, description, unit, is_active, category, partition_types: [{partition_type_id, is_required}] }
 */
export const selectMetricForEdit = (metricId) => (state) => {
  const metric = selectMetricById(state, metricId);
  if (!metric) return null;

  return {
    id: metric.id,
    name: metric.name,
    description: metric.description || '',
    // Map backend metric_type to frontend unit for form display
    unit: mapMetricTypeToUnit(metric.metric_type) || metric.unit || 'Count',
    metric_type: metric.metric_type || 'COUNT',
    aggregation_mode: metric.aggregation_mode || 'sum',
    calculation_config: metric.calculation_config || {},
    activity_counting_available: metric.activity_counting_available === true,
    is_active: metric.is_active !== false,
    category: metric.category?.id || metric.category || null,
    partition_types: (metric._metricPartitions || metric.partition_configurations || []).map(mp => ({
      partition_type_id: mp.partition_type?.id || mp.partition_type_id || mp.partition_type,
      partition_type_name: mp.partition_type_name || mp.name,
      is_required: mp.is_required || false,
    })),
    // Passed through (not reshaped) so pages built on this selector - e.g.
    // MetricEditPageNew's "Enter Data" button - can gate on the Rock
    // manual-entry lock without a second selector (FE-4, design doc §7).
    rock_binding_summary: metric.rock_binding_summary || null,
    // The server's manual-entry lock (utils/metricEntryLock); undefined keeps
    // the older aggregation/Rock fallback for payloads that predate it.
    manual_entry_lock: metric.manual_entry_lock,
    // Confidential-metric access; the audience lists reach owners/admins only.
    access: metric.access || 'organization',
    allowed_roles: metric.allowed_roles || [],
    allowed_user_ids: metric.allowed_user_ids || [],
    audience_members: metric.audience_members || [],
    is_restricted: metric.is_restricted === true,
  };
};

/**
 * Selector to get default values for metric creation wizard
 * Returns default form values for new metric
 */
export const selectMetricCreationDefaults = () => ({
  name: '',
  description: '',
  unit: 'Count',
  aggregation_mode: 'sum',
  calculation_config: {},
  is_active: true,
  category_id: null,
  partition_types: [],
});

export default metricsSlice.reducer;
